<template>
  <el-dialog
    title="Вход в банк (Infin)"
    :visible.sync="localShow"
    :width="isMobile ? '95%' : '520px'"
    top="8vh"
    @open="onOpen"
    @close="onClose"
    custom-class="native-auth-dialog"
  >
    <!-- Error / stop message -->
    <div v-if="errorMsg" class="alert alert-danger" role="alert">
      <span class="alert-text">{{ errorMsg }}</span>
    </div>

    <!-- Step indicator -->
    <p class="step-hint" v-if="stage !== 'done'">{{ stageHint }}</p>

    <!-- Generated device summary (shown once the attempt has started) -->
    <p v-if="deviceProfile && stage !== 'passport'" class="device-profile">
      <i class="ni ni-mobile-button"></i>
      Устройство: {{ deviceProfile.model }} · iOS {{ deviceProfile.ios_version }} · прил. {{ deviceProfile.app_version }}
    </p>

    <!-- 1. Passport + device profile -->
    <div v-if="stage === 'passport'">
      <div class="row">
        <div class="col-6">
          <base-input label="Серия (2 буквы)" placeholder="AB" v-model="form.passport_serial" />
          <span v-if="form.passport_serial && !validSerial" class="field-err">2 латинские буквы</span>
        </div>
        <div class="col-6">
          <base-input label="Номер (7 цифр)" placeholder="0123456" v-model="form.passport_number" />
          <span v-if="form.passport_number && !validNumber" class="field-err">7 цифр</span>
        </div>
      </div>
      <base-input label="Дата рождения (дд.мм.гггг)" placeholder="05.09.1990" v-model="form.birth_date" />
      <span v-if="form.birth_date && !validBirth" class="field-err">Формат дд.мм.гггг</span>

      <p class="device-note auto-note">
        <i class="ni ni-mobile-button"></i>
        Профиль устройства (модель, ID, версия iOS и приложения) подставляется автоматически под реальный iPhone — вводить ничего не нужно.
      </p>

      <div class="device-toggle" @click="showDevice = !showDevice">
        <i :class="showDevice ? 'ni ni-bold-up' : 'ni ni-bold-down'"></i>
        Дополнительно
      </div>
      <div v-show="showDevice" class="device-section">
        <base-checkbox v-model="regenerateDevice">Сгенерировать новое устройство</base-checkbox>
        <base-checkbox v-model="manualDevice">Задать устройство вручную</base-checkbox>
        <div v-if="manualDevice" class="mt-2">
          <p class="device-note">Значения — примеры формата; при ручном вводе отключите «Сгенерировать новое устройство».</p>
          <div class="row">
            <div class="col-6"><base-input label="Модель (deviceId)" placeholder="iPhone 16e" v-model="device.device_model" /></div>
            <div class="col-6"><base-input label="Версия iOS" placeholder="18.6.2" v-model="device.ios_version" /></div>
          </div>
          <div class="row">
            <div class="col-6"><base-input label="Serial" placeholder="UUID" v-model="device.device_serial" /></div>
            <div class="col-6"><base-input label="Версия приложения" placeholder="3.16.0" v-model="device.app_version" /></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. Phone -->
    <div v-else-if="stage === 'phone'">
      <base-input label="Номер телефона (998XXXXXXXXX)" placeholder="998901234567" v-model="phone" />
      <span v-if="phone && !validPhone" class="field-err">12 цифр, начинается с 998</span>
    </div>

    <!-- 3. SMS -->
    <div v-else-if="stage === 'sms'">
      <p v-if="phoneMask" class="mask">Код отправлен на {{ phoneMask }}</p>
      <base-input label="SMS-код" placeholder="001234" v-model="smsCode" />
      <span v-if="smsCode && !validSms" class="field-err">4–8 цифр</span>
      <a class="resend-link" @click="resend" :class="{ disabled: loading }">Отправить код повторно</a>
    </div>

    <!-- 4. Face capture -->
    <div v-else-if="stage === 'face'">
      <p class="device-note">
        Сканирование лица для шага FACE_ID. Камера работает прямо в браузере, снимок кодируется в Base64 автоматически.
        Банк может требовать штатный liveness-SDK — если обычный снимок не принимается, вставьте результат SDK вручную ниже.
      </p>

      <face-scanner v-model="photoBase64" :provider-id="providerId" @busy="faceProcessing = $event" />
    </div>

    <!-- 5. FaceIDS SDK -->
    <div v-else-if="stage === 'faceids'">
      <p class="device-note">Поле <b>signature</b> из результата FaceIDS SDK (не весь объект и не фото). Отправляется как {"signature": …} на check-faceids-passport.</p>
      <textarea class="form-control code-area" rows="4" v-model="faceidsSignature" placeholder="signature…"></textarea>
    </div>

    <!-- 6. Trust device -->
    <div v-else-if="stage === 'device'">
      <base-checkbox v-model="trustDevice">Доверять этому устройству (isAddDevice)</base-checkbox>
    </div>

    <!-- 7. Done -->
    <div v-else-if="stage === 'done'" class="done-box">
      <i class="ni ni-check-bold"></i>
      <p>Авторизация завершена — токен получен.</p>
    </div>

    <!-- Footer -->
    <span slot="footer" class="dialog-footer">
      <base-button type="secondary" @click="closeModal">Закрыть</base-button>

      <base-button v-if="stage === 'passport'" type="primary" :disabled="loading || !validPassport" @click="start">
        <i v-if="loading" class="el-icon-loading"></i> Начать вход
      </base-button>
      <base-button v-else-if="stage === 'phone'" type="primary" :disabled="loading || !validPhone" @click="submit('phone', phone)">
        <i v-if="loading" class="el-icon-loading"></i> Отправить
      </base-button>
      <base-button v-else-if="stage === 'sms'" type="primary" :disabled="loading || !validSms" @click="submit('sms', smsCode)">
        <i v-if="loading" class="el-icon-loading"></i> Подтвердить
      </base-button>
      <base-button v-else-if="stage === 'face'" type="primary" :disabled="loading || faceProcessing || !cleanPhoto" @click="submit('face', cleanPhoto)">
        <i v-if="loading" class="el-icon-loading"></i> Отправить
      </base-button>
      <base-button v-else-if="stage === 'faceids'" type="primary" :disabled="loading" @click="submit('faceids', faceidsSignature)">
        <i v-if="loading" class="el-icon-loading"></i> Отправить
      </base-button>
      <base-button v-else-if="stage === 'device'" type="primary" :disabled="loading" @click="submit('device', trustDevice)">
        <i v-if="loading" class="el-icon-loading"></i> Отправить
      </base-button>

      <base-button v-if="attemptId" type="warning" :disabled="loading" @click="restart">
        Начать заново
      </base-button>
    </span>
  </el-dialog>
</template>

<script>
import infinAuthRequests from '@/utils/infinAuthRequests';
import BaseInput from '@/components/Inputs/BaseInput.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseCheckbox from '@/components/Inputs/BaseCheckbox.vue';
import FaceScanner from '@/components/Modals/FaceScanner.vue';

const ERROR_TEXT = {
  DEVICE_CONFIG_REQUIRED: 'Заполните параметры устройства (модель, serial, версия iOS, версия приложения).',
  API_DECLINED: 'Банк отклонил запрос (status 3999). Попробуйте позже — причина не раскрывается.',
  SESSION_TIMEOUT: 'Сессия истекла. Начните вход заново.',
  REGISTRATION_REQUIRED: 'Сервер направил на регистрацию — это не вход. Автоматически регистрацию не продолжаем; появление этого шага не означает, что клиента нет в базе (решает сервер).',
  RETURNED_TO_LOGIN: 'Сервер вернул на экран входа — доступ не подтверждён.',
  DOCUMENT_BRANCH: 'Назначена ветка сканирования документа — ручной ввод её не покрывает.',
  UNKNOWN_NEXT_STEP: 'Неизвестный следующий шаг — вход не завершён.',
  VERIFY_FAILED: 'Проверка токена (customer/data) не прошла.',
  HTTP_ERROR: 'Ошибка запроса к банку.',
  STATUS_ERROR: 'Банк вернул ошибку.',
  BAD_RESPONSE: 'Некорректный ответ банка.',
  SMS_RESEND_FAILED: 'Не удалось отправить код повторно.',
  STEP_MISMATCH: 'Этот шаг сейчас не ожидается сервером.',
  NATIVE_AUTH_DISABLED: 'Нативный вход недоступен для этого провайдера.',
  AUTH_IN_PROGRESS: 'Дождитесь завершения текущего запроса Infin.',
  AUTH_ATTEMPT_ACTIVE: 'Уже существует незавершённая попытка входа.',
  AUTH_OUTCOME_UNKNOWN: 'Результат запроса неизвестен. Повтор временно запрещён.',
  ATTEMPT_MISMATCH: 'Это окно относится к другой попытке. Откройте вход заново.',
  ATTEMPT_EXPIRED: 'Попытка истекла. Начните вход заново.',
};

const STAGE_HINT = {
  passport: 'Шаг 1. Реквизиты документа',
  phone: 'Введите номер телефона',
  sms: 'Подтвердите SMS-код',
  face: 'Проверка лица (штатный захват)',
  faceids: 'Проверка лица (FaceIDS SDK)',
  device: 'Доверие устройству',
};

export default {
  name: 'NativeAuthModal',
  mixins: [infinAuthRequests],
  components: { BaseInput, BaseButton, BaseCheckbox, FaceScanner },
  props: {
    show: { type: Boolean, default: false },
    providerId: { type: Number, default: null },
  },
  data() {
    return {
      stage: 'passport',
      errorCode: '',
      errorMsg: '',
      phoneMask: '',
      canRestart: false,
      showDevice: false,
      manualDevice: false,
      regenerateDevice: false,
      deviceProfile: null,
      form: { birth_date: '', passport_serial: '', passport_number: '' },
      device: { device_model: '', device_serial: '', ios_version: '', app_version: '' },
      phone: '',
      smsCode: '',
      photoBase64: '',
      faceProcessing: false,
      faceidsSignature: '',
      trustDevice: true,
    };
  },
  computed: {
    localShow: {
      get() { return this.show; },
      set(v) { this.$emit('update:show', v); },
    },
    isMobile() { return window.innerWidth < 576; },
    stageHint() { return STAGE_HINT[this.stage] || ''; },
    // What is actually sent to the bank: strip any data: prefix (camera capture
    // is already clean; this guards a manual paste that includes the prefix).
    cleanPhoto() { return (this.photoBase64 || '').replace(/^data:[^;]+;base64,/, '').trim(); },

    // Live validation (validates as you type, masks enforce the shape)
    validSerial() { return /^[A-Z]{2}$/.test(this.form.passport_serial); },
    validNumber() { return /^\d{7}$/.test(this.form.passport_number); },
    validBirth() {
      const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(this.form.birth_date);
      if (!m) return false;
      const d = +m[1], mo = +m[2], y = +m[3];
      return d >= 1 && d <= 31 && mo >= 1 && mo <= 12 && y >= 1900 && y <= 2015;
    },
    validPassport() { return this.validSerial && this.validNumber && this.validBirth; },
    validPhone() { return /^998\d{9}$/.test(this.phone); },
    validSms() { return /^\d{4,8}$/.test(this.smsCode); },
  },
  watch: {
    // Input masks: sanitize to the allowed shape on every keystroke.
    'form.passport_serial'(v) {
      const s = (v || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
      if (s !== v) this.form.passport_serial = s;
    },
    'form.passport_number'(v) {
      const s = (v || '').replace(/\D/g, '').slice(0, 7);
      if (s !== v) this.form.passport_number = s;
    },
    'form.birth_date'(v) {
      const s = this.maskBirth(v || '');
      if (s !== v) this.form.birth_date = s;
    },
    phone(v) {
      const s = (v || '').replace(/\D/g, '').slice(0, 12);
      if (s !== v) this.phone = s;
    },
    smsCode(v) {
      const s = (v || '').replace(/\D/g, '').slice(0, 8);
      if (s !== v) this.smsCode = s;
    },
    'device.ios_version'(v) {
      const s = (v || '').replace(/[^\d.]/g, '').slice(0, 10);
      if (s !== v) this.device.ios_version = s;
    },
    'device.app_version'(v) {
      const s = (v || '').replace(/[^\d.]/g, '').slice(0, 10);
      if (s !== v) this.device.app_version = s;
    },
  },
  methods: {
    resetAuthForm() {
      this.stage = 'passport';
      this.errorCode = '';
      this.errorMsg = '';
      this.phoneMask = '';
      this.canRestart = false;
      this.smsCode = '';
      this.photoBase64 = '';
      this.faceProcessing = false;
      this.faceidsSignature = '';
      this.deviceProfile = null;
    },

    /** Mask free input into dd.mm.yyyy as the user types. */
    maskBirth(v) {
      const d = v.replace(/\D/g, '').slice(0, 8);
      let out = d.slice(0, 2);
      if (d.length > 2) out += '.' + d.slice(2, 4);
      if (d.length > 4) out += '.' + d.slice(4, 8);
      return out;
    },

    async start() {
      this.clearError();
      const payload = {
        birth_date: this.form.birth_date.trim(),
        passport_serial: this.form.passport_serial.trim(),
        passport_number: this.form.passport_number.trim(),
      };
      if (this.manualDevice) {
        // Advanced: operator-supplied device profile.
        payload.device = {
          device_model: this.device.device_model.trim(),
          device_serial: this.device.device_serial.trim(),
          ios_version: this.device.ios_version.trim(),
          app_version: this.device.app_version.trim(),
        };
      } else if (this.regenerateDevice) {
        // Force a fresh auto-generated device profile.
        payload.regenerate_device = true;
      }
      await this.post(`dashboard/providers/${this.providerId}/native-auth/start`, payload, true);
    },

    async submit(screen, value) {
      this.clearError();
      await this.post(`dashboard/providers/${this.providerId}/native-auth/step`, { screen, value });
    },

    async resend() {
      if (this.loading) return;
      this.clearError();
      await this.post(`dashboard/providers/${this.providerId}/native-auth/step`, { screen: 'resend_sms' });
    },

    handleResult(data, isStart) {
      if (data && data.phone_mask) this.phoneMask = data.phone_mask;
      if (data && data.device_profile && data.device_profile.model) this.deviceProfile = data.device_profile;

      if (data && data.status === true) {
        if (data.done) {
          if (isStart) { this.stage = 'done'; return; }
          this.completeAuthView();
          return;
        }
        if (data.screen) this.stage = data.screen;
        // a non-advancing success (e.g. resend) keeps the current stage
        return;
      }

      // Failure
      this.setError(data ? data.error : null, data ? data.message : null);
      // Device config missing → keep operator on the passport stage with fields open
      if (data && data.error === 'DEVICE_CONFIG_REQUIRED') {
        this.stage = 'passport';
        this.showDevice = true;
      }
      // Stop conditions → allow a clean restart
      if (data && ['SESSION_TIMEOUT', 'RETURNED_TO_LOGIN', 'REGISTRATION_REQUIRED', 'DOCUMENT_BRANCH', 'UNKNOWN_NEXT_STEP', 'API_DECLINED', 'VERIFY_FAILED'].includes(data.error)) {
        this.canRestart = true;
      }
      // Wrong step: realign to what the server expects, if it told us
      if (data && data.error === 'STEP_MISMATCH' && data.screen) {
        this.stage = data.screen;
      }
    },

    setError(code, serverMsg) {
      this.errorCode = code || 'HTTP_ERROR';
      const base = ERROR_TEXT[this.errorCode] || 'Ошибка входа.';
      this.errorMsg = serverMsg ? `${base} (${serverMsg})` : base;
    },
    clearError() { this.errorCode = ''; this.errorMsg = ''; },
    closeModal() { this.localShow = false; },
  },
};
</script>

<style scoped>
.step-hint { font-weight: 600; color: #32325d; margin-bottom: 1rem; }
.device-toggle { cursor: pointer; color: #5e72e4; font-size: 0.85rem; margin: 0.5rem 0; user-select: none; }
.device-section { background: #f7fafc; border-radius: 0.375rem; padding: 0.75rem; margin-bottom: 0.5rem; }
.device-note { font-size: 0.78rem; color: #8898aa; margin-bottom: 0.5rem; }
.auto-note { background: #eef2ff; border-radius: 0.375rem; padding: 0.6rem 0.75rem; color: #4b5bbf; margin: 0.75rem 0; }
.device-profile { font-size: 0.82rem; color: #525f7f; background: #f7fafc; border-radius: 0.375rem; padding: 0.5rem 0.75rem; margin-bottom: 0.9rem; }
.mask { font-size: 0.85rem; color: #525f7f; margin-bottom: 0.5rem; }
.resend-link { display: inline-block; margin-top: 0.5rem; color: #5e72e4; cursor: pointer; font-size: 0.85rem; }
.resend-link.disabled { color: #adb5bd; pointer-events: none; }
.code-area { width: 100%; font-family: monospace; font-size: 0.8rem; }
.field-err { display: block; color: #f5365c; font-size: 0.72rem; margin: -0.5rem 0 0.6rem; }
.face-scanner { text-align: center; margin-bottom: 0.75rem; }
.face-media { width: 220px; max-width: 100%; height: 220px; object-fit: cover; border-radius: 0.5rem; background: #1f2435; }
.face-media.mirrored { transform: scaleX(-1); }
.mirror-toggle { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.8rem; color: #525f7f; margin-top: 0.5rem; cursor: pointer; }
.mirror-toggle input { margin: 0; }
.face-placeholder { width: 220px; max-width: 100%; height: 220px; margin: 0 auto; border-radius: 0.5rem; background: #f7fafc; border: 1px dashed #ced4da; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #8898aa; gap: 0.4rem; }
.face-placeholder i { font-size: 2rem; }
.face-error { color: #f5365c; font-size: 0.78rem; margin: 0.4rem 0 0; }
.face-actions { display: flex; align-items: center; justify-content: center; gap: 0.75rem; margin-top: 0.6rem; }
.face-ok { color: #2dce89; font-size: 0.85rem; }
.face-canvas { display: none; }
.done-box { text-align: center; padding: 1.5rem 0; color: #2dce89; }
.done-box i { font-size: 2.5rem; }
.dialog-footer { display: flex; justify-content: flex-end; gap: 0.5rem; flex-wrap: wrap; }
.alert { margin-bottom: 1rem; }
@media (max-width: 576px) {
  .dialog-footer { flex-direction: column-reverse; }
  .dialog-footer button { width: 100%; margin: 0 !important; }
}
</style>
